import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import sortBy from 'lodash.sortby';
import { Box } from '@mui/material';
import logger from '@widgets/logger';
import ChannelsCreateModal from '@features/ChannelsCreateModal';
import {
    useChannels,
    useCouponsTemplates,
    useIntlLocale,
    useLocations,
    usePermissions,
    useSurveysOptions,
    useTranslation,
} from '@hooks';
import { Select } from '@components/Select';
import { Input } from '@components/Input';
import { Switch } from '@components/Switch';
import { RadioButtonsGroup } from '@components/RadioButtonsGroup';
import { MultiSelectLabels } from '@components/MultiSelectLabels';
import TextEditor from '@components/TextEditor';
import ImageUploaderSingle from '@components/ImageUploaderSingle';
import { Typography } from '@components/Typography';
import PostDynamicVariables from '@features/PostDynamicVariables';
import { AITaskType, AI_TASK_TYPE } from '@constants/ai-assistant';
import {
    AUTOMATION_DATE_SOURCE,
    AUTOMATION_EVENT_TRIGGERS,
    AUTOMATION_INTEGRATION_TRIGGERS,
    AUTOMATION_NODE_TYPE,
    AUTOMATION_TRIGGER_OPTIONS,
    collapseTriggerIds,
    CUSTOM_EVENT_TRIGGER,
    eventTriggerId,
    expandTriggerIds,
    findEventTrigger,
    integrationTriggerId,
    INTEGRATION_TRIGGER,
    isEventTriggerId,
    SEGMENT_TRIGGER,
    TRIGGER_SOURCE_TYPE,
} from '@constants/automations';
import { isIntegrationConnected } from '@helpers/integrations';
import { generateTimeOptions, toMinutes, toTime } from '@helpers/dates';
import { monthOptions } from '@helpers/contact-demographics';
import { PLACEHOLDER_CONTEXT } from '@constants/placeholders';
import { CHANNEL, STATUS } from '@constants/channels';
import { MB } from '@constants/file';
import { isSurveySendable } from '@constants/surveys';
import { validateFileSize } from '@helpers/upload-file';
import { parseTipTapJSONToPlainText } from '@helpers/text-editor';
import { alertActions } from '@actions';
import { campaignsService } from '@services';
import TemplatePicker from '../../EditModal/TemplatePicker';
import WhatsAppTemplateSection from '../../EditModal/WhatsAppTemplateSection';
import TriggerEventsSelect from '../../TriggerEventsSelect';
import ConditionBuilder from './ConditionBuilder';
import { useEventFieldOptions } from './eventFieldOptions';
import { WAIT_UNITS, splitWait } from './wait';
import {
    DATE_TIMING,
    defaultDateTrigger,
    MAX_OFFSET_DAYS,
    daysInMonth,
    firstYearAhead,
    offsetFor,
    sendsAfter,
    timingOf,
    toLocalMinute,
    toUtcMinute,
} from './dateTrigger';
import { SUPPORTED_CHANNEL_TYPES, isUsableChannel } from './validation';

const SectionLabel = ({ children }) => (
    <Typography variant="subtitle2" color="textSecondary">
        {children}
    </Typography>
);

const getEventTrigger = (activityTypes, customEventType) =>
    (activityTypes || []).includes(CUSTOM_EVENT_TRIGGER) ? findEventTrigger(customEventType) : null;

const DateTriggerFields = ({ date, onChange }) => {
    const { t } = useTranslation();
    const intlLocale = useIntlLocale();
    const months = useMemo(() => monthOptions(intlLocale), [intlLocale]);
    const sendTime = toTime(toLocalMinute(date.sendMinute));
    const times = useMemo(() => {
        const options = generateTimeOptions();
        return options.some(option => option.value === sendTime)
            ? options
            : [...options, { value: sendTime, label: sendTime }].sort((a, b) => a.value.localeCompare(b.value));
    }, [sendTime]);

    const isFixed = date.source === AUTOMATION_DATE_SOURCE.FIXED_DATE;
    const timing = timingOf(date.offsetDays);
    const offsetDays = Math.abs(date.offsetDays);
    const [offsetText, setOffsetText] = useState(String(offsetDays || 1));
    const thisYear = new Date().getFullYear();
    const years = [
        ...new Set([...[thisYear, thisYear + 1, thisYear + 2].filter(year => sendsAfter(date, year)), date.year]),
    ]
        .filter(Boolean)
        .sort((a, b) => a - b);
    const days = Array.from({ length: date.month ? daysInMonth(date.month, date.year) : 31 }, (_, index) => ({
        value: index + 1,
        label: `${index + 1}`,
    }));

    const change = patch => {
        const next = { ...date, ...patch };
        const lastDay = next.month ? daysInMonth(next.month, next.year) : null;
        onChange(lastDay && next.day > lastDay ? { ...next, day: lastDay } : next);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Select
                label={t('AutomationsPage.trigger.date')}
                value={date.source}
                onChange={e =>
                    change({
                        source: Number(e.target.value),
                        month: null,
                        day: null,
                        year: null,
                    })
                }
                options={[
                    { value: AUTOMATION_DATE_SOURCE.BIRTHDAY, label: t('AutomationsPage.trigger.contactBirthday') },
                    { value: AUTOMATION_DATE_SOURCE.FIXED_DATE, label: t('AutomationsPage.trigger.fixedDate') },
                ]}
                fullWidth
            />
            {isFixed && (
                <>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Select
                            style={{ flex: 2 }}
                            label={t('AutomationsPage.trigger.month')}
                            value={date.month ?? ''}
                            onChange={e => change({ month: Number(e.target.value) })}
                            options={months}
                            fullWidth
                        />
                        <Select
                            style={{ flex: 1 }}
                            label={t('AutomationsPage.trigger.day')}
                            value={date.day ?? ''}
                            onChange={e => change({ day: Number(e.target.value) })}
                            options={days}
                            fullWidth
                        />
                    </Box>
                    <Switch
                        checked={date.year == null}
                        handleChange={e => change({ year: e.target.checked ? null : firstYearAhead(date) })}
                        label={t('AutomationsPage.trigger.everyYear')}
                    />
                    {date.year != null && (
                        <Select
                            label={t('AutomationsPage.trigger.year')}
                            value={date.year}
                            onChange={e => change({ year: Number(e.target.value) })}
                            options={years.map(year => ({ value: year, label: `${year}` }))}
                            fullWidth
                        />
                    )}
                </>
            )}
            <Select
                label={t('AutomationsPage.trigger.timing')}
                value={timing}
                onChange={e => {
                    const days = Math.min(MAX_OFFSET_DAYS, Math.max(1, Number(offsetText) || offsetDays || 1));
                    setOffsetText(String(days));
                    change({ offsetDays: offsetFor(e.target.value, days) });
                }}
                options={[
                    { value: DATE_TIMING.ON, label: t('AutomationsPage.trigger.onTheDay') },
                    { value: DATE_TIMING.BEFORE, label: t('AutomationsPage.trigger.before') },
                    { value: DATE_TIMING.AFTER, label: t('AutomationsPage.trigger.after') },
                ]}
                fullWidth
            />
            {timing !== DATE_TIMING.ON && (
                <Input
                    type="number"
                    label={t(
                        timing === DATE_TIMING.BEFORE
                            ? 'AutomationsPage.trigger.offsetDaysBefore'
                            : 'AutomationsPage.trigger.offsetDaysAfter',
                    )}
                    inputProps={{
                        value: offsetText,
                        min: 1,
                        max: MAX_OFFSET_DAYS,
                        inputMode: 'numeric',
                        onChange: e => {
                            const text = e.target.value;
                            const days = Number(text);
                            setOffsetText(text);
                            if (Number.isInteger(days) && days >= 1 && days <= MAX_OFFSET_DAYS) {
                                change({ offsetDays: offsetFor(timing, days) });
                            }
                        },
                        onBlur: () => setOffsetText(String(offsetDays || offsetText)),
                    }}
                    fullWidth
                />
            )}
            <Select
                label={t('AutomationsPage.trigger.sendAt')}
                value={sendTime}
                onChange={e => change({ sendMinute: toUtcMinute(toMinutes(e.target.value)) })}
                options={times}
                fullWidth
            />
        </Box>
    );
};

const TriggerSettings = ({ node, base, setBase, update, labels }) => {
    const { t } = useTranslation();
    const account = useSelector(state => state.account?.account);
    const eventFieldOptions = useEventFieldOptions();
    const data = node.data;
    const eventTrigger = getEventTrigger(data.activityTypes, data.customEventType);

    // A bare Integration activity type with no narrowing means "all integrations".
    const rawIntegrations = data.integrationTypes || [];
    const selectedIntegrations = rawIntegrations.length
        ? rawIntegrations
        : (data.activityTypes || []).includes(INTEGRATION_TRIGGER)
          ? AUTOMATION_INTEGRATION_TRIGGERS.map(o => o.integration)
          : [];

    const triggerOptions = [
        ...AUTOMATION_TRIGGER_OPTIONS.filter(o => o.value !== INTEGRATION_TRIGGER).map(o => ({
            value: o.value,
            label: t(`AutomationsPage.triggers.${o.key}`),
        })),
        ...AUTOMATION_INTEGRATION_TRIGGERS.filter(
            o => isIntegrationConnected(account, o.integration) || selectedIntegrations.includes(o.integration),
        ).map(o => ({
            value: integrationTriggerId(o.integration),
            label: t(`AutomationsPage.triggers.${o.key}`),
        })),
        ...AUTOMATION_EVENT_TRIGGERS.filter(
            o =>
                o.integrations.some(integration => isIntegrationConnected(account, integration)) ||
                eventTrigger?.eventType === o.eventType,
        ).map(o => ({
            value: eventTriggerId(o.eventType),
            label: t(`AutomationsPage.triggers.${o.key}`),
        })),
    ];

    const triggerValue = expandTriggerIds(data.activityTypes, selectedIntegrations, data.customEventType);

    const onTriggerChange = selected => {
        const added = selected.filter(id => !triggerValue.includes(id));
        const addedEventTrigger = added.find(isEventTriggerId);
        const next = addedEventTrigger
            ? selected.filter(id => id !== CUSTOM_EVENT_TRIGGER && (!isEventTriggerId(id) || id === addedEventTrigger))
            : added.includes(CUSTOM_EVENT_TRIGGER)
              ? selected.filter(id => !isEventTriggerId(id))
              : selected;
        const collapsed = collapseTriggerIds(next);
        update({
            ...collapsed,
            customEventType: collapsed.customEventType ?? (eventTrigger ? '' : data.customEventType),
            segmentIds: collapsed.activityTypes.includes(SEGMENT_TRIGGER) ? data.segmentIds || [] : [],
        });
    };

    const startsOnDate = data.sourceType === TRIGGER_SOURCE_TYPE.DATE;
    const segmentIds = data.segmentIds || [];

    const onModeChange = sourceType =>
        update(
            sourceType === TRIGGER_SOURCE_TYPE.DATE
                ? {
                      sourceType,
                      activityTypes: [],
                      integrationTypes: [],
                      customEventType: '',
                      segmentIds: [],
                      date: data.date || defaultDateTrigger(),
                  }
                : { sourceType, date: null },
        );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <RadioButtonsGroup
                value={startsOnDate ? TRIGGER_SOURCE_TYPE.DATE : TRIGGER_SOURCE_TYPE.CONTACT_ACTIVITY}
                onChange={onModeChange}
                options={[
                    {
                        value: TRIGGER_SOURCE_TYPE.CONTACT_ACTIVITY,
                        label: t('AutomationsPage.trigger.byEvent'),
                    },
                    { value: TRIGGER_SOURCE_TYPE.DATE, label: t('AutomationsPage.trigger.byDate') },
                ]}
            />

            {startsOnDate ? (
                <DateTriggerFields date={data.date || defaultDateTrigger()} onChange={date => update({ date })} />
            ) : (
                <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TriggerEventsSelect
                            value={triggerValue}
                            onChange={onTriggerChange}
                            options={triggerOptions}
                            allOption={!triggerOptions.some(option => isEventTriggerId(option.value))}
                        />
                        {(data.activityTypes || []).includes(CUSTOM_EVENT_TRIGGER) && !eventTrigger && (
                            <Input
                                label={t('AutomationsPage.drawer.customEventType')}
                                inputProps={{
                                    value: data.customEventType || '',
                                    onChange: e => update({ customEventType: e.target.value }),
                                }}
                                fullWidth
                            />
                        )}
                        {(data.activityTypes || []).includes(SEGMENT_TRIGGER) && (
                            <MultiSelectLabels
                                label={t('AutomationsPage.trigger.whichSegments')}
                                value={segmentIds}
                                onChange={ids => update({ segmentIds: ids })}
                                options={[
                                    ...labels.map(label => ({ value: label.id, label: label.name })),
                                    ...segmentIds
                                        .filter(id => !labels.some(label => label.id === id))
                                        .map(id => ({
                                            value: id,
                                            label: t('AutomationsPage.conditions.deletedSegment'),
                                        })),
                                ]}
                                editable={false}
                            />
                        )}
                    </Box>

                    <Box>
                        <Switch
                            checked={!!base.onePerContact}
                            handleChange={e => setBase({ ...base, onePerContact: e.target.checked })}
                            label={t('AutomationsPage.editor.oncePerContact')}
                        />
                    </Box>
                </>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <SectionLabel>{t('AutomationsPage.trigger.whoCanEnter')}</SectionLabel>
                <ConditionBuilder
                    key={node.id}
                    value={base.entryConditions}
                    onChange={next => setBase({ ...base, entryConditions: next })}
                    labels={labels}
                    allowEventField={(data.activityTypes || []).includes(CUSTOM_EVENT_TRIGGER)}
                    eventFields={eventTrigger?.fields}
                    eventFieldOptions={eventFieldOptions}
                />
            </Box>
        </Box>
    );
};

const contentDefaults = data => ({
    channelId: data.channelId || '',
    message: data.message || '',
    emailTemplateId: data.emailTemplateId || '',
    whatsAppTemplateRegistryId: data.whatsAppTemplateRegistryId || '',
    whatsAppTemplateId: data.whatsAppTemplateId || '',
    couponTemplateId: data.couponTemplateId || '',
    surveyTemplateId: data.surveyTemplateId || '',
    coverPhotoEnabled: !!data.coverPhotoEnabled,
    coverPhotoUrl: data.coverPhotoUrl || '',
});

const toNodePatch = values => ({
    ...values,
    channelId: values.channelId || null,
    emailTemplateId: values.emailTemplateId || null,
    whatsAppTemplateRegistryId: values.whatsAppTemplateRegistryId || null,
    whatsAppTemplateId: values.whatsAppTemplateId || null,
    couponTemplateId: values.couponTemplateId || null,
    surveyTemplateId: values.surveyTemplateId || null,
    coverPhotoUrl: values.coverPhotoUrl || null,
});

const SendStepSettings = ({ node, update, children }) => {
    const methods = useForm({ defaultValues: contentDefaults(node.data) });

    useEffect(() => {
        const subscription = methods.watch(values => update(toNodePatch(values)));
        return () => subscription.unsubscribe();
    }, []);

    return <FormProvider {...methods}>{children}</FormProvider>;
};

const useSelectedChannelType = () => {
    const { watch } = useFormContext();
    const { channels } = useChannels();
    const channelId = watch('channelId');
    return channels.find(channel => channel.id === channelId)?.type;
};

const StepChannelSelect = ({ isView }) => {
    const { t } = useTranslation();
    const { control, setValue, watch } = useFormContext();
    const { channels, getAccessibleChannelIds, getChannelOptions } = useChannels();
    const { hasFullSettings } = usePermissions();
    const [createModal, setCreateModal] = useState({
        open: false,
        step: 1,
        type: CHANNEL.SMS,
        status: STATUS.PENDING,
        qrCode: '',
        liveChatPublicId: null,
    });
    const [createdChannel, setCreatedChannel] = useState();

    const options = useMemo(() => {
        const availableChannels = getAccessibleChannelIds(channel => SUPPORTED_CHANNEL_TYPES.includes(channel.type));
        return sortBy(getChannelOptions({ out: true }, availableChannels), ['name']);
    }, [channels]);

    const selected = watch('channelId');
    const selectedChannel = channels.find(channel => channel.id === selected);
    const selectedName = selectedChannel?.name || selected;
    const missingFromOptions = !!selected && !options.some(option => option.value === selected);
    const selectedUnavailable = !!selected && channels.length > 0 && !isUsableChannel(selectedChannel);
    const optionsWithFallback = missingFromOptions
        ? [...options, { value: selected, label: selectedName, name: selectedName, disabled: true }]
        : options;

    return (
        <>
            <Controller
                control={control}
                name="channelId"
                render={({ field, ref }) => (
                    <Select
                        {...field}
                        ref={ref}
                        onChange={({ target: { value } }) => {
                            if (value !== 'create-new') {
                                setValue('emailTemplateId', '');
                                setValue('whatsAppTemplateRegistryId', '');
                                setValue('whatsAppTemplateId', '');
                                setValue('channelId', value, { shouldDirty: true, shouldValidate: true });
                            }
                        }}
                        label={t('common.channel')}
                        options={optionsWithFallback}
                        required
                        disabled={isView}
                        fullWidth
                        error={selectedUnavailable}
                        helperText={selectedUnavailable ? t('AutomationsPage.issues.channelUnavailable') : null}
                        creatable={!isView && hasFullSettings()}
                        creatableText={t('SettingsChannels.createNewChannel')}
                        creatableClick={() => setCreateModal(prev => ({ ...prev, open: true }))}
                    />
                )}
            />
            {createModal.open ? (
                <ChannelsCreateModal
                    modal={createModal}
                    setModal={setCreateModal}
                    channel={createdChannel}
                    setChannel={setCreatedChannel}
                />
            ) : null}
        </>
    );
};

const MessageEditor = ({ channelType, isView }) => {
    const { t } = useTranslation();
    const { control, setValue } = useFormContext();
    const { getLocationsOptions, locationsMap } = useLocations();

    const aiContextLocationId = useMemo(() => getLocationsOptions()[0]?.id, [locationsMap]);

    return (
        <Controller
            control={control}
            name="message"
            render={({ field, fieldState: { error }, ref }) => (
                <div style={{ position: 'relative' }}>
                    <TextEditor
                        {...field}
                        ref={ref}
                        label={t('common.message', { count: 1 })}
                        limit={channelType === CHANNEL.WHATSAPP ? 1024 : 0}
                        placeholderContext={PLACEHOLDER_CONTEXT.CAMPAIGN}
                        initialValue={field.value || ''}
                        onChange={text =>
                            setValue('message', parseTipTapJSONToPlainText(text), {
                                shouldValidate: true,
                                shouldDirty: true,
                            })
                        }
                        editable={!isView}
                        required
                        error={error}
                        helperText={error ? t(error.message, { count: 1024 }) : null}
                        toolbar={<PostDynamicVariables disabled={isView} />}
                        aiAssistant={{
                            additionalOptions: [
                                {
                                    text: t('AIAssistant.writeCampaign'),
                                    type: AITaskType.GenerateCampaignMessage,
                                    taskType: AI_TASK_TYPE.CONTEXTUAL,
                                    companyId: aiContextLocationId,
                                },
                            ],
                        }}
                    />
                </div>
            )}
        />
    );
};

const CouponSelect = ({ isView }) => {
    const { t } = useTranslation();
    const { control, setValue } = useFormContext();
    const { getCouponsOptions } = useCouponsTemplates();

    const couponsOptions = useMemo(() => getCouponsOptions(), [getCouponsOptions]);

    return (
        <Controller
            control={control}
            name="couponTemplateId"
            render={({ field, fieldState: { error }, ref }) => (
                <Select
                    {...field}
                    ref={ref}
                    onChange={({ target: { value } }) =>
                        setValue('couponTemplateId', value, { shouldDirty: true, shouldValidate: true })
                    }
                    options={couponsOptions}
                    required
                    disabled={isView}
                    label={t('common.coupon')}
                    fullWidth
                    error={!!error}
                    helperText={error ? t(error.message) : null}
                />
            )}
        />
    );
};

const SurveySelect = ({ isView }) => {
    const { t } = useTranslation();
    const { control, setValue } = useFormContext();
    const { getSurveysOptions } = useSurveysOptions();

    const surveysOptions = useMemo(() => getSurveysOptions().filter(isSurveySendable), [getSurveysOptions]);

    return (
        <Controller
            control={control}
            name="surveyTemplateId"
            render={({ field, fieldState: { error }, ref }) => (
                <Select
                    {...field}
                    ref={ref}
                    onChange={({ target: { value } }) =>
                        setValue('surveyTemplateId', value, { shouldDirty: true, shouldValidate: true })
                    }
                    options={surveysOptions}
                    required
                    disabled={isView}
                    label={t('common.survey')}
                    fullWidth
                    error={!!error}
                    helperText={error ? t(error.message) : null}
                />
            )}
        />
    );
};

const CoverPhotoSection = ({ isView }) => {
    const [uploading, setUploading] = useState(false);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { setValue, watch } = useFormContext();

    const coverPhotoEnabled = watch('coverPhotoEnabled');
    const coverPhotoUrl = watch('coverPhotoUrl');

    const uploadImage = async e => {
        try {
            const file = e.target.files[0];
            if (!file) return;
            const { error, metric, size } = validateFileSize(file, 15 * MB);
            if (error) {
                dispatch(alertActions.error(t('apiErrors.file_size_too_large', { size, metric })));
                return;
            }
            setUploading(true);
            const data = new FormData();
            data.append('file', file);
            data.append('fileName', file.name);
            const response = await campaignsService.uploadEmailCampaignImage(data);
            setValue('coverPhotoUrl', response.publicUrl, { shouldDirty: true, shouldValidate: true });
        } catch (_) {
            logger.error(`Couldn't upload coverPhotoUrl`);
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    return (
        <>
            <Switch
                checked={!!coverPhotoEnabled}
                handleChange={event =>
                    setValue('coverPhotoEnabled', event.target.checked, {
                        shouldDirty: true,
                        shouldValidate: true,
                    })
                }
                label={t('CampaignsPage.addImage')}
                disabled={isView}
            />
            {coverPhotoEnabled && (
                <div>
                    <Typography
                        variant="caption"
                        sx={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}
                    >
                        {t('CampaignsPage.imageFormat')}
                    </Typography>
                    <ImageUploaderSingle
                        extensions=".png, .jpg, .jpeg"
                        logoUrl={coverPhotoUrl}
                        maxSize={5 * MB}
                        disabled={uploading || isView}
                        onDelete={() => setValue('coverPhotoUrl', null, { shouldDirty: true, shouldValidate: true })}
                        uploadLogo={uploadImage}
                        isUploading={uploading}
                    />
                </div>
            )}
        </>
    );
};

const MessageFields = ({ isCoupon, isView }) => {
    const channelType = useSelectedChannelType();
    const isEmail = channelType === CHANNEL.EMAIL;
    const isWhatsAppBusiness = channelType === CHANNEL.WHATSAPP_BUSINESS;
    const isWhatsAppBeta = channelType === CHANNEL.WHATSAPP;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <StepChannelSelect isView={isView} />
            {isCoupon && <CouponSelect isView={isView} />}
            {isEmail && (
                <TemplatePicker
                    isView={isView}
                    requiresDynamicLink={isCoupon}
                    requiredLinkType={isCoupon ? 'coupon' : null}
                />
            )}
            {isWhatsAppBusiness && (
                <WhatsAppTemplateSection isView={isView} requiresLink={isCoupon} isCouponCampaign={isCoupon} />
            )}
            {!isEmail && !isWhatsAppBusiness && <MessageEditor channelType={channelType} isView={isView} />}
            {isWhatsAppBeta && <CoverPhotoSection isView={isView} />}
        </Box>
    );
};

const SurveyFields = ({ isView }) => {
    const channelType = useSelectedChannelType();
    const isEmail = channelType === CHANNEL.EMAIL;
    const isWhatsAppBusiness = channelType === CHANNEL.WHATSAPP_BUSINESS;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <StepChannelSelect isView={isView} />
            <SurveySelect isView={isView} />
            {isEmail && <TemplatePicker isView={isView} requiresDynamicLink requiredLinkType="survey" />}
            {isWhatsAppBusiness && <WhatsAppTemplateSection isView={isView} requiresLink isCouponCampaign={false} />}
        </Box>
    );
};

const WaitSettings = ({ node, update }) => {
    const { t } = useTranslation();
    const [unitFactor, setUnitFactor] = useState(() => splitWait(node.data.waitMinutes).unit.factor);
    const amount = node.data.waitMinutes ? node.data.waitMinutes / unitFactor : 0;
    return (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Input
                type="number"
                label={t('AutomationsPage.drawer.wait')}
                inputProps={{
                    value: amount || '',
                    onChange: e => update({ waitMinutes: Math.max(0, Number(e.target.value)) * unitFactor }),
                }}
                fullWidth
            />
            <Select
                value={unitFactor}
                onChange={e => {
                    setUnitFactor(Number(e.target.value));
                    update({ waitMinutes: (amount || 0) * Number(e.target.value) });
                }}
                options={WAIT_UNITS.map(u => ({ value: u.factor, label: t(`AutomationsPage.units.${u.key}`) }))}
            />
        </Box>
    );
};

const BranchSettings = ({ node, update, labels, triggerActivityTypes, triggerCustomEventType }) => {
    const { t } = useTranslation();
    const eventFieldOptions = useEventFieldOptions();
    const eventTrigger = getEventTrigger(triggerActivityTypes, triggerCustomEventType);
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <SectionLabel>{t('AutomationsPage.drawer.branchHint')}</SectionLabel>
            <ConditionBuilder
                key={node.id}
                value={node.data.conditions}
                onChange={next => update({ conditions: next })}
                labels={labels}
                allowEventField={(triggerActivityTypes || []).includes(CUSTOM_EVENT_TRIGGER)}
                eventFields={eventTrigger?.fields}
                eventFieldOptions={eventFieldOptions}
            />
        </Box>
    );
};

const NodeSettings = ({
    node,
    base,
    setBase,
    updateNodeData,
    labels,
    readOnly,
    triggerActivityTypes,
    triggerCustomEventType,
}) => {
    const { t } = useTranslation();
    const update = patch => updateNodeData(node.id, { ...node.data, ...patch });

    switch (node.data.type) {
        case AUTOMATION_NODE_TYPE.TRIGGER:
            return <TriggerSettings node={node} base={base} setBase={setBase} update={update} labels={labels} />;
        case AUTOMATION_NODE_TYPE.SEND_MESSAGE:
            return (
                <SendStepSettings key={node.id} node={node} update={update}>
                    <MessageFields isCoupon={!!node.data.isCoupon} isView={readOnly} />
                </SendStepSettings>
            );
        case AUTOMATION_NODE_TYPE.SEND_SURVEY:
            return (
                <SendStepSettings key={node.id} node={node} update={update}>
                    <SurveyFields isView={readOnly} />
                </SendStepSettings>
            );
        case AUTOMATION_NODE_TYPE.WAIT:
            return <WaitSettings key={node.id} node={node} update={update} />;
        case AUTOMATION_NODE_TYPE.BRANCH:
            return (
                <BranchSettings
                    node={node}
                    update={update}
                    labels={labels}
                    triggerActivityTypes={triggerActivityTypes}
                    triggerCustomEventType={triggerCustomEventType}
                />
            );
        case AUTOMATION_NODE_TYPE.EXIT:
            return (
                <Typography variant="body2" color="textSecondary">
                    {t('AutomationsPage.nodes.exitSubtitle')}
                </Typography>
            );
        default:
            return null;
    }
};

export default NodeSettings;
